
import React, { useState } from 'react';
import { createEmployee } from '../../services/adminService';
import './CreateEmployeeModal.css';

const CreateEmployeeModal = ({ onClose, onSuccess }) => {
  // Comprehensive list of all organization roles
  const allDesignations = [
    // Engineering
    "Intern",
    "Trainee",
    "Junior Software Engineer",
    "Software Engineer",
    "Senior Software Engineer",
    "Lead Software Engineer",
    "Staff Software Engineer",
    "Principal Engineer",
    "Engineering Manager",
    "Senior Engineering Manager",
    "Director of Engineering",
    "VP of Engineering",

    // Data
    "Junior Data Analyst",
    "Data Analyst",
    "Senior Data Analyst",
    "Lead Analyst",
    "Analytics Manager",
    "Principal Data Analyst",

    // Product
    "Associate Product Manager",
    "Product Manager",
    "Senior Product Manager",
    "Group Product Manager",
    "Director of Product",
    "VP of Product",

    // Design
    "Junior UI/UX Designer",
    "UI/UX Designer",
    "Senior UI/UX Designer",
    "Lead UI/UX Designer",
    "Design Manager",

    // HR
    "HR Intern",
    "HR Executive",
    "Senior HR Executive",
    "HR Generalist",
    "HR Manager",
    "Senior HR Manager",
    "Director of HR",

    // Marketing & Sales
    "Marketing Intern",
    "Marketing Associate",
    "Marketing Executive",
    "Senior Marketing Executive",
    "Marketing Manager",
    "Sales Intern",
    "Sales Executive",
    "Sales Manager",
    "Regional Sales Manager"
  ].sort();

  const allDepartments = [
    'Engineering', 'Product', 'Design', 'HR', 'Marketing', 'Sales', 'General', 'Development QA', 'Operations', 'Finance', 'Support'
  ].sort();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    salary: '',
    workHours: '',
    employeeType: 'Full Time',
    designation: '',
    department: 'General',
    profilePhoto: '',
    employeeId: '',
    bankName: '',
    accountNo: '',
    panNumber: '',
    uanNumber: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [createdEmployee, setCreatedEmployee] = useState(null);

  // 👈 NEW: Autocomplete State
  const [filteredDesignations, setFilteredDesignations] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [showDeptSuggestions, setShowDeptSuggestions] = useState(false);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setError('Photo size must be less than 2MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, profilePhoto: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Handle Designation Autocomplete
    if (name === 'designation') {
      if (value.trim()) {
        const filtered = allDesignations.filter(d =>
          d.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredDesignations(filtered);
        setShowSuggestions(true);
      } else {
        setFilteredDesignations([]);
        setShowSuggestions(false);
      }
    }

    // Handle Department Autocomplete
    if (name === 'department') {
      if (value.trim()) {
        const filtered = allDepartments.filter(d =>
          d.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredDepartments(filtered);
        setShowDeptSuggestions(true);
      } else {
        setFilteredDepartments([]);
        setShowDeptSuggestions(false);
      }
    }
  };

  const handleSuggestionClick = (designation) => {
    setFormData({ ...formData, designation });
    setShowSuggestions(false);
  };

  const handleDeptSuggestionClick = (department) => {
    setFormData({ ...formData, department });
    setShowDeptSuggestions(false);
  };

  // Close suggestions when clicking outside (handled slightly by blur or overlap, but for now simple)
  // Check CSS for positioning

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await createEmployee({
        name: formData.name,
        email: formData.email,
        salary: formData.salary ? parseFloat(formData.salary) : 0,
        workHours: formData.workHours ? parseFloat(formData.workHours) : 8,
        employeeType: formData.employeeType,
        designation: formData.designation,
        department: formData.department,
        employeeId: formData.employeeId,
        profilePhoto: formData.profilePhoto,
        bankName: formData.bankName,
        accountNo: formData.accountNo,
        panNumber: formData.panNumber,
        uanNumber: formData.uanNumber
      });
      setCreatedEmployee(result.employee);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (createdEmployee) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <h2>Employee Created Successfully!</h2>
          <div className="employee-credentials">
            <p><strong>Name:</strong> {createdEmployee.name}</p>
            <p><strong>Email:</strong> {createdEmployee.email}</p>
            <p><strong>Password:</strong> <span className="password-display">{createdEmployee.password}</span></p>
            <p className="warning">⚠️ Please save this password. It won't be shown again!</p>
          </div>
          <div className="modal-actions">
            <button onClick={onSuccess} className="btn-primary">Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>Create New Employee</h2>
        <form onSubmit={handleSubmit} autoComplete="off">
          {error && <div className="error-message">{error}</div>}
          <div className="modal-body">
            
            {/* Profile Photo Upload */}
            <div className="form-group photo-upload-group" style={{ textAlign: 'center', marginBottom: '1rem' }}>
              <div style={{
                width: '100px', height: '100px', borderRadius: '50%', backgroundColor: '#e2e8f0',
                margin: '0 auto 10px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                overflow: 'hidden', border: '2px solid #cbd5e1'
              }}>
                {formData.profilePhoto ? (
                  <img src={formData.profilePhoto} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <span style={{ fontSize: '2rem', color: '#94a3b8' }}>👤</span>
                )}
              </div>
              <label style={{ cursor: 'pointer', color: '#2563eb', fontSize: '0.875rem', fontWeight: '500' }}>
                Upload Profile Photo
                <input 
                  type="file" 
                  accept="image/jpeg, image/png, image/webp" 
                  style={{ display: 'none' }} 
                  onChange={handlePhotoChange}
                />
              </label>
              {formData.profilePhoto && (
                <div style={{ marginTop: '5px' }}>
                  <button type="button" onClick={() => setFormData({...formData, profilePhoto: ''})} style={{ fontSize: '0.75rem', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}>
                    Remove Photo
                  </button>
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Employee name"
              />
            </div>
            <div className="form-group">
              <label>Employee ID *</label>
              <input
                type="text"
                name="employeeId"
                value={formData.employeeId}
                onChange={handleChange}
                required
                placeholder="e.g. EMP001"
              />
            </div>
            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="employee@example.com"
              />
            </div>
            <div className="form-group">
              <label>Salary *</label>
              <input
                type="number"
                name="salary"
                value={formData.salary}
                onChange={handleChange}
                placeholder="0"
                min="0"
                step="0.01"
                required
              />
            </div>
            <div className="form-group">
              <label>Work Hours (per day) *</label>
              <input
                type="number"
                name="workHours"
                value={formData.workHours}
                onChange={handleChange}
                placeholder="8"
                min="0"
                max="24"
                step="0.5"
                required
              />
            </div>
            <div className="form-group">
              <label>Employee Type *</label>
              <select
                name="employeeType"
                value={formData.employeeType}
                onChange={handleChange}
                required
              >
                <option value="Full Time">Full Time</option>
                <option value="Intern">Intern</option>
                <option value="Contract">Contract</option>
              </select>
            </div>
            <div className="form-group" style={{ position: 'relative' }}>
              <label>Designation *</label>
              <input
                type="text"
                name="designation"
                value={formData.designation}
                onChange={handleChange}
                onFocus={(e) => {
                  if (e.target.value) setShowSuggestions(true);
                }}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                placeholder="Select or Type Designation"
                required
                autoComplete="off"
              />
              {showSuggestions && filteredDesignations.length > 0 && (
                <ul className="suggestions-list">
                  {filteredDesignations.map((role) => (
                    <li key={role} onClick={() => handleSuggestionClick(role)}>
                      {role}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            
            <div className="form-group" style={{ position: 'relative' }}>
              <label>Department *</label>
              <input
                type="text"
                name="department"
                value={formData.department}
                onChange={handleChange}
                onFocus={(e) => {
                  if (e.target.value) setShowDeptSuggestions(true);
                }}
                onBlur={() => setTimeout(() => setShowDeptSuggestions(false), 200)}
                placeholder="Select or Type Department"
                required
                autoComplete="off"
              />
              {showDeptSuggestions && filteredDepartments.length > 0 && (
                <ul className="suggestions-list">
                  {filteredDepartments.map((dept) => (
                    <li key={dept} onClick={() => handleDeptSuggestionClick(dept)}>
                      {dept}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            
            <div className="form-group">
              <label>Bank Name</label>
              <input
                type="text"
                name="bankName"
                value={formData.bankName}
                onChange={handleChange}
                placeholder="Bank Name"
              />
            </div>
            <div className="form-group">
              <label>Account No</label>
              <input
                type="text"
                name="accountNo"
                value={formData.accountNo}
                onChange={handleChange}
                placeholder="Account No"
              />
            </div>
            <div className="form-group">
              <label>Pan Number</label>
              <input
                type="text"
                name="panNumber"
                value={formData.panNumber}
                onChange={handleChange}
                placeholder="Pan Number"
              />
            </div>
            <div className="form-group">
              <label>UAN Number</label>
              <input
                type="text"
                name="uanNumber"
                value={formData.uanNumber}
                onChange={handleChange}
                placeholder="UAN Number"
              />
            </div>
          </div>
          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? 'Creating...' : 'Create Employee'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateEmployeeModal;